import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';
import '../styles/components/Admin.css';

const Admin = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dashboard state
  const [dashboardData, setDashboardData] = useState(null);

  // Countries state
  const [countries, setCountries] = useState([]);
  const [countrySearch, setCountrySearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');

  // Stations state
  const [stations, setStations] = useState([]);
  const [stationSearch, setStationSearch] = useState('');
  const [stationFilter, setStationFilter] = useState('all');

  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboard();
    } else if (activeTab === 'countries') {
      loadCountries();
    } else if (activeTab === 'stations') {
      loadStations();
    }
  }, [activeTab]);

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.getAdminDashboard();
      if (response.success) {
        setDashboardData(response.dashboard);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (err) {
      setError('Error loading dashboard: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.getAdminCountries();
      if (response.success) {
        setCountries(response.countries);
      } else {
        setError('Failed to load countries');
      }
    } catch (err) {
      setError('Error loading countries: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStations = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.getAdminStations();
      if (response.success) {
        setStations(response.stations);
      } else {
        setError('Failed to load stations');
      }
    } catch (err) {
      setError('Error loading stations: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleCountryStatus = async (countryId) => {
    try {
      const response = await apiService.toggleCountryStatus(countryId);
      if (response.success) {
        setSuccess(response.message);
        loadCountries();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Error updating country status: ' + err.message);
    }
  };

  const toggleStationStatus = async (stationId) => {
    try {
      const response = await apiService.toggleStationStatus(stationId);
      if (response.success) {
        setSuccess(response.message);
        loadStations();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Error updating station status: ' + err.message);
    }
  };

  const filteredCountries = countries.filter(country => {
    const matchesSearch = country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                         country.iso_code.toLowerCase().includes(countrySearch.toLowerCase());
    const matchesFilter = countryFilter === 'all' || 
                         (countryFilter === 'active' && country.is_active) ||
                         (countryFilter === 'inactive' && !country.is_active);
    return matchesSearch && matchesFilter;
  });

  const filteredStations = stations.filter(station => {
    const matchesSearch = station.name.toLowerCase().includes(stationSearch.toLowerCase()) ||
                         station.country_name.toLowerCase().includes(stationSearch.toLowerCase());
    const matchesFilter = stationFilter === 'all' || 
                         (stationFilter === 'active' && station.is_active) ||
                         (stationFilter === 'inactive' && !station.is_active);
    return matchesSearch && matchesFilter;
  });

  const renderDashboard = () => {
    if (!dashboardData) return <div>Loading dashboard...</div>;

    const { stats, recent_activity, top_performers } = dashboardData;

    return (
      <div className="admin-dashboard">
        <h2>Admin Dashboard</h2>
        
        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Visitors</h3>
            <p className="stat-number">{stats.total_visitors.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h3>Total Plays</h3>
            <p className="stat-number">{stats.total_plays.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h3>Total Stations</h3>
            <p className="stat-number">{stats.total_stations.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h3>Total Countries</h3>
            <p className="stat-number">{stats.total_countries.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h3>Active Stations</h3>
            <p className="stat-number">{stats.active_stations.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h3>Active Countries</h3>
            <p className="stat-number">{stats.active_countries.toLocaleString()}</p>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="dashboard-section">
          <h3>Recent Activity</h3>
          <div className="recent-activity">
            <div className="recent-countries">
              <h4>Recent Countries</h4>
              <ul>
                {recent_activity.recent_countries.slice(0, 5).map(country => (
                  <li key={country.id}>
                    {country.name} - {country.station_count} stations
                  </li>
                ))}
              </ul>
            </div>
            <div className="recent-stations">
              <h4>Recent Stations</h4>
              <ul>
                {recent_activity.recent_stations.slice(0, 5).map(station => (
                  <li key={station.id}>
                    {station.name} ({station.country_name}) - {station.success_rate}% success
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Top Performers */}
        <div className="dashboard-section">
          <h3>Top Performers</h3>
          <div className="top-performers">
            <div className="top-countries">
              <h4>Top Countries by Plays</h4>
              <ul>
                {top_performers.top_countries.slice(0, 5).map(country => (
                  <li key={country.id}>
                    {country.name} - {country.play_count} plays
                  </li>
                ))}
              </ul>
            </div>
            <div className="top-stations">
              <h4>Top Stations by Plays</h4>
              <ul>
                {top_performers.top_stations.slice(0, 5).map(station => (
                  <li key={station.id}>
                    {station.name} ({station.country_name}) - {station.play_count} plays
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCountries = () => (
    <div className="admin-countries">
      <div className="admin-header">
        <h2>Manage Countries</h2>
        <div className="admin-controls">
          <input
            type="text"
            placeholder="Search countries..."
            value={countrySearch}
            onChange={(e) => setCountrySearch(e.target.value)}
            className="search-input"
          />
          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Countries</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
      </div>

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>ISO Code</th>
              <th>Stations</th>
              <th>Plays</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCountries.map(country => (
              <tr key={country.id}>
                <td>{country.name}</td>
                <td>{country.iso_code}</td>
                <td>{country.station_count}</td>
                <td>{country.play_count}</td>
                <td>
                  <span className={`status ${country.is_active ? 'active' : 'inactive'}`}>
                    {country.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <button
                    onClick={() => toggleCountryStatus(country.id)}
                    className={`toggle-btn ${country.is_active ? 'deactivate' : 'activate'}`}
                  >
                    {country.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderStations = () => (
    <div className="admin-stations">
      <div className="admin-header">
        <h2>Manage Stations</h2>
        <div className="admin-controls">
          <input
            type="text"
            placeholder="Search stations..."
            value={stationSearch}
            onChange={(e) => setStationSearch(e.target.value)}
            className="search-input"
          />
          <select
            value={stationFilter}
            onChange={(e) => setStationFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Stations</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
      </div>

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Country</th>
              <th>Plays</th>
              <th>Errors</th>
              <th>Success Rate</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStations.map(station => (
              <tr key={station.id}>
                <td>{station.name}</td>
                <td>{station.country_name}</td>
                <td>{station.play_count}</td>
                <td>{station.error_count}</td>
                <td>{station.success_rate}%</td>
                <td>
                  <span className={`status ${station.is_active ? 'active' : 'inactive'}`}>
                    {station.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <button
                    onClick={() => toggleStationStatus(station.id)}
                    className={`toggle-btn ${station.is_active ? 'deactivate' : 'activate'}`}
                  >
                    {station.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="admin-panel">
      <div className="admin-sidebar">
        <div className="admin-logo">
          <Link to="/">RadioStreamIn</Link>
        </div>
        <nav className="admin-nav">
          <button
            className={activeTab === 'dashboard' ? 'active' : ''}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
          <button
            className={activeTab === 'countries' ? 'active' : ''}
            onClick={() => setActiveTab('countries')}
          >
            Countries
          </button>
          <button
            className={activeTab === 'stations' ? 'active' : ''}
            onClick={() => setActiveTab('stations')}
          >
            Stations
          </button>
        </nav>
      </div>

      <div className="admin-content">
        <div className="admin-header-bar">
          <h1>Admin Panel</h1>
          <Link to="/" className="back-to-app">← Back to App</Link>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
            <button onClick={() => setSuccess('')}>×</button>
          </div>
        )}

        {loading && <div className="loading">Loading...</div>}

        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'countries' && renderCountries()}
        {activeTab === 'stations' && renderStations()}
      </div>
    </div>
  );
};

export default Admin;
